import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Wrench, Zap } from "lucide-react";
import { CHANGELOG_ENTRIES, CHANGELOG_DATE, type ChangelogEntryType } from "@/lib/changelog";

const typeConfig: Record<ChangelogEntryType, {
  label: string;
  icon: React.ElementType;
  iconClass: string;
  badgeClass: string;
}> = {
  feature: {
    label: "New",
    icon: Sparkles,
    iconClass: "bg-primary/15 text-primary",
    badgeClass: "bg-primary/10 text-primary border-transparent",
  },
  improvement: {
    label: "Improvement",
    icon: Zap,
    iconClass: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    badgeClass: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-transparent",
  },
  fix: {
    label: "Fix",
    icon: Wrench,
    iconClass: "bg-green-500/15 text-green-700 dark:text-green-400",
    badgeClass: "bg-green-500/10 text-green-700 dark:text-green-400 border-transparent",
  },
};

interface WhatsNewModalProps {
  open: boolean;
  onDismiss: () => void;
}

export function WhatsNewModal({ open, onDismiss }: WhatsNewModalProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onDismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="modal-whats-new">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mb-2">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center">What's New</DialogTitle>
          <DialogDescription className="text-center">{CHANGELOG_DATE}</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] mt-2">
          <ul className="space-y-4 pr-3">
            {CHANGELOG_ENTRIES.map((entry, i) => {
              const { label, icon: Icon, iconClass, badgeClass } = typeConfig[entry.type];
              return (
                <li key={i} className="flex gap-3">
                  <div className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${iconClass}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-sm font-medium">{entry.title}</span>
                      <Badge variant="outline" className={`text-xs py-0 ${badgeClass}`}>{label}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{entry.description}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
        <Button className="w-full mt-4" onClick={onDismiss} data-testid="button-whats-new-dismiss">
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
}
